/**
 * Unified autocomplete for admin-group assignment fields (animators, managers).
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCoordinators } from '@/services/users.service';

/** Options for {@link useAdminAutocomplete}. */
interface UseAdminAutocompleteOptions {
  /** Restrict results to specific role codes. Omit for all admin-group users. */
  roleCodes?: string[];
  /** Optional program name filter (passed to the API). */
  programName?: string | null;
  /** Optional manager ID filter (for coordinators under a manager). */
  managedById?: string;
  /**
   * Gate the query until a condition is met (e.g. a modal is open).
   * Defaults to true. The query always requires at least 2 characters typed.
   */
  enabled?: boolean;
}

/**
 * Unified autocomplete hook for admin-group user assignment fields.
 *
 * Replaces useAnimatorsAutocomplete + usePairManagerOptions.
 *
 * Usage:
 *   RI / field-event animators → useAdminAutocomplete({ roleCodes: ['recruiter_remote'] })
 *   Welcome-meeting animators → useAdminAutocomplete({ roleCodes: ['coordinator_remote'] })
 *   Tutoring "managed by" → useAdminAutocomplete({ roleCodes: ['coordinator_remote', 'manager_remote', 'admin'], enabled: sessionType === 'tutorat' })
 *   Pair manager → useAdminAutocomplete({ roleCodes: ['coordinator_remote', 'manager_remote', 'admin'], programName: pair?.program, enabled: open })
 *   Animator filter → useAdminAutocomplete()
 */
export const useAdminAutocomplete = ({
  roleCodes,
  programName,
  managedById,
  enabled = true,
}: UseAdminAutocompleteOptions = {}) => {
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [accumulatedData, setAccumulatedData] = useState<any[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'admin-autocomplete',
      roleCodes ?? [],
      programName ?? '',
      managedById ?? '',
      debouncedQuery,
      page,
    ],
    queryFn: () =>
      getCoordinators({
        search: debouncedQuery || undefined,
        page,
        pageSize: 10,
        roleCodes,
        programName: programName ?? undefined,
        managedById,
      }),
    enabled: enabled && debouncedQuery.length >= 2,
  });

  /** Accumulate pages as they load. */
  useMemo(() => {
    if (!data?.data) return;

    if (page === 1) {
      setAccumulatedData(data.data);
    } else {
      setAccumulatedData((prev) => {
        const existingIds = new Set(prev.map((u) => u.id));
        const uniqueNew = data.data.filter((u: any) => !existingIds.has(u.id));
        return [...prev, ...uniqueNew];
      });
    }
  }, [data, page]);

  const options = useMemo(
    () =>
      accumulatedData.map((u) => ({
        value: u.id,
        label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.id,
      })),
    [accumulatedData],
  );

  const onSearch = useCallback((query: string) => {
    setTimeout(() => {
      setDebouncedQuery(query);
      setPage(1);
      setAccumulatedData([]);
    }, 300);
  }, []);

  const onLoadMore = useCallback(() => {
    if (data && page * 10 < data.total) {
      setPage((p) => p + 1);
    }
  }, [data, page]);

  const hasMore = Boolean(data && page * 10 < data.total);

  return { options, isLoading, onSearch, onLoadMore, hasMore };
};
